import type { ReactNode } from "react";

import type { AgenticLogicTemplateSummaryDto } from "@intelligems/ig-types/functions/agentic-logic/templates/list";
import { IgAlert } from "@intelligems/ig-theme/IgAlert";
import { IgLoading } from "@intelligems/ig-theme/IgLoading";
import { IgPaper } from "@intelligems/ig-theme/IgPaper";
import { IgTypography } from "@intelligems/ig-theme/IgTypography";
import { ButtonBase, Stack, useTheme } from "@mui/material";
import Grid from "@mui/material/Grid";

import { AskAtlas } from "@src/features/ai/components/AskAtlas/AskAtlas";

import { SURFACE_LABEL, visibleSurfaces } from "../rules/surfaceLabels";

export interface CreateRuleFormProps {
  prompt: string;
  onPromptChange: (prompt: string) => void;
  onBuild: () => void;
  onSelectTemplate: (template: AgenticLogicTemplateSummaryDto) => void;
  templates: AgenticLogicTemplateSummaryDto[];
  templatesLoading: boolean;
  templatesError?: boolean;
  onReloadTemplates?: () => void;
  canBuild: boolean;
  ruleIdeas: readonly string[];
  /**
   * A short example rule for this store, generated with the rule ideas, that
   * the empty composer shows after "e.g.". Null until it has been generated.
   */
  placeholderExample: string | null;
  /** Use the compact generated example where the composer width is limited. */
  compactPlaceholder?: boolean;
  attachmentControls?: ReactNode;
  attachmentPreview?: ReactNode;
  /**
   * How wide a template card is, in the gallery's ten columns. The gallery is
   * a full page on one surface and a dialog on another, and only the card
   * width differs between them.
   */
  templateCardSize?: { xs: number; sm?: number; lg?: number };
  /**
   * Whether the Ask Atlas composer carries its own card. The page frames it as
   * one — it is the page's primary block; the dialog is already that frame.
   *
   * `"none"` drops the composer entirely, for the embedded surface: the Atlas
   * rail is already open beside the gallery there, so a second composer would
   * offer the same thing twice and only one of them would carry the transcript.
   */
  composerSurface?: "paper" | "plain" | "none";
  /**
   * Heading for the templates section. The embedded surface says "or start
   * from a template" in its own subtitle, so it passes null rather than
   * repeating it here.
   */
  templatesHeading?: ReactNode;
}

// Shown until this store's own example has been generated. One line of the
// field, like the generated example that replaces it.
const DEFAULT_PLACEHOLDER_EXAMPLE =
  "Fill the gap to free shipping with the cheapest add-on";
const FULL_PAGE_PLACEHOLDER =
  "Describe what you want to recommend — which products, in what order, and for which shoppers";

const visuallyHiddenSx = {
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: "1px",
  overflow: "hidden",
  position: "absolute",
  whiteSpace: "nowrap",
  width: "1px",
} as const;

/**
 * Everything a rule author chooses between when creating a rule: the Atlas
 * composer and the template gallery. Atlas is the only way in — a template
 * card starts the chat from that template; there is no manual configuration
 * or write-the-code-yourself entry here.
 *
 * Layout only — every decision is a callback. It is shared verbatim by the
 * creation page and the create-rule dialog, which differ in their frame and in
 * how wide the template cards sit, not in what they offer.
 */
export const CreateRuleForm = ({
  prompt,
  onPromptChange,
  onBuild,
  onSelectTemplate,
  templates,
  templatesLoading,
  templatesError = false,
  onReloadTemplates,
  canBuild,
  ruleIdeas,
  placeholderExample,
  compactPlaceholder = false,
  attachmentControls,
  attachmentPreview,
  templateCardSize = { xs: 10, sm: 5, lg: 2 },
  composerSurface = "paper",
  templatesHeading = "Or start from a template",
}: CreateRuleFormProps) => {
  const theme = useTheme();
  const showTemplateCards =
    !templatesLoading && !templatesError && templates.length > 0;

  const entryCardSx = () => ({
    alignItems: "stretch",
    bgcolor: theme.igBackgroundColors.baseLightHigh,
    border: `1px solid ${theme.igBorders.core.default}`,
    borderRadius: "6px",
    height: "100%",
    justifyContent: "flex-start",
    overflow: "hidden",
    // 15px inside the 1px border: Figma's stroke sits inside the card's 16px
    // padding, so the card is 75px tall rather than 77px.
    p: "15px",
    textAlign: "left",
    transition: "border-color 140ms ease-out, box-shadow 140ms ease-out",
    width: "100%",
    "&:hover": {
      borderColor: theme.igBorders.core.hover,
      boxShadow: "0 1px 2px 0 rgba(0,0,0,0.05)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.igBorders.core.active}`,
      outlineOffset: -2,
    },
  });

  const composer =
    composerSurface === "none" ? null : (
      <AskAtlas
        title="Ask Atlas to build a rule"
        prompt={prompt}
        onPromptChange={onPromptChange}
        onSubmit={onBuild}
        canSubmit={canBuild}
        submitLabel="Build rule"
        placeholder={
          compactPlaceholder
            ? `e.g. ${placeholderExample ?? DEFAULT_PLACEHOLDER_EXAMPLE}`
            : FULL_PAGE_PLACEHOLDER
        }
        inputLabel="Describe your rule"
        suggestions={ruleIdeas}
        attachmentControls={attachmentControls}
        attachmentPreview={attachmentPreview}
      />
    );

  return (
    // The page sets its composer card and the template section 32px apart;
    // the dialog is its own frame and keeps them closer.
    <Stack spacing={composerSurface === "paper" ? 8 : 3}>
      {composerSurface === "none" ? null : composerSurface === "paper" ? (
        <IgPaper
          aria-label="Atlas rule builder"
          role="region"
          sx={{
            borderColor: theme.igBorders.core.default,
            borderRadius: "6px",
            p: 5,
          }}
        >
          {composer}
        </IgPaper>
      ) : (
        <Stack aria-label="Atlas rule builder" role="region">
          {composer}
        </Stack>
      )}

      <Stack spacing={4}>
        {templatesHeading ? (
          <Stack direction="row" alignItems="center">
            <IgTypography
              component="h2"
              variant="body1"
              fontWeight={600}
              sx={{ minWidth: 0, lineHeight: 1.5 }}
            >
              {templatesHeading}
            </IgTypography>
          </Stack>
        ) : null}

        {templatesError ? (
          <Stack role="alert">
            <IgAlert
              alertKey="agentic-logic-template-catalog-error"
              severity="error"
              title="Templates could not be loaded"
              message="You can still ask Atlas for a custom rule, or reload the template catalog."
              primaryButton={
                onReloadTemplates
                  ? { cta: "Reload templates", onClick: onReloadTemplates }
                  : undefined
              }
            />
          </Stack>
        ) : templatesLoading ? (
          <Stack
            alignItems="center"
            aria-atomic="true"
            aria-live="polite"
            role="status"
            sx={{ py: 5 }}
          >
            <IgLoading />
            <IgTypography sx={visuallyHiddenSx}>Loading templates</IgTypography>
          </Stack>
        ) : templates.length === 0 ? (
          <Stack aria-atomic="true" aria-live="polite" role="status">
            <IgAlert
              alertKey="agentic-logic-template-catalog-empty"
              severity="info"
              title="No templates are available"
              message="You can still ask Atlas to build a custom rule above."
              primaryButton={
                onReloadTemplates
                  ? { cta: "Reload templates", onClick: onReloadTemplates }
                  : undefined
              }
            />
          </Stack>
        ) : null}

        {showTemplateCards ? (
          // The dialog's two 395px columns sit 12px apart, which is exactly its
          // 802px content width; the page keeps its wider 14px gutter.
          <Grid
            container
            columns={10}
            spacing={composerSurface === "plain" ? 3 : 3.5}
          >
            {templates.map((template) => {
              const titleId = `agentic-logic-template-${template.id}-title`;
              const descriptionId = `agentic-logic-template-${template.id}-description`;
              const surfacesId = `agentic-logic-template-${template.id}-surfaces`;
              const actionId = `agentic-logic-template-${template.id}-action`;
              return (
                <Grid key={template.id} size={templateCardSize}>
                  <ButtonBase
                    aria-describedby={`${descriptionId} ${surfacesId} ${actionId}`}
                    aria-labelledby={titleId}
                    onClick={() => onSelectTemplate(template)}
                    sx={entryCardSx()}
                  >
                    <Stack spacing={1} sx={{ width: "100%" }}>
                      <IgTypography
                        id={titleId}
                        variant="body1"
                        noWrap
                        sx={{ lineHeight: "21px" }}
                      >
                        {template.title}
                      </IgTypography>
                      <IgTypography
                        id={descriptionId}
                        variant="body2"
                        color="igSecondary"
                      >
                        {template.description}
                      </IgTypography>
                      <IgTypography
                        id={surfacesId}
                        variant="body2"
                        sx={visuallyHiddenSx}
                      >
                        {visibleSurfaces(template.eligibleSurfaces)
                          .map((surface) => SURFACE_LABEL[surface])
                          .join(" · ")}
                      </IgTypography>
                      <IgTypography
                        id={actionId}
                        variant="body2"
                        fontWeight={600}
                        sx={visuallyHiddenSx}
                      >
                        Start with Atlas
                      </IgTypography>
                    </Stack>
                  </ButtonBase>
                </Grid>
              );
            })}
          </Grid>
        ) : null}
      </Stack>
    </Stack>
  );
};
